import { forwardRef, useState } from 'react';

import { useTranslations } from 'next-intl';

import { ActionIcon, Badge, Button, CopyButton, Group, Paper, Skeleton, Stack, Text, Textarea, ThemeIcon, Tooltip, TypographyStylesProvider } from '@mantine/core';
import { IconBolt, IconCheck, IconCopy, IconEdit, IconFileText, IconFileTypeCsv, IconFileTypePdf, IconPaperclip, IconPhoto, IconSparkles } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { ChatAttachment } from '@repo/contracts';

import { UiTreeRenderer } from '@components/chatbot/UiTreeRenderer';

interface ChatMessageBubbleProps {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    attachments?: ChatAttachment[];
    isLastUserMessage?: boolean;
    onEditSubmit?: (newContent: string) => void;
    onActionClick?: (key: string, label: string) => void;
    size?: 'sm' | 'md' | 'lg';
}

/**
 * Design d'une bulle de discussion et parsing Markdown.
 * forwardRef permet au composant parent d'ancrer le défilement sur cette bulle.
 */
export const ChatMessageBubble = forwardRef<HTMLDivElement, ChatMessageBubbleProps>(
    ({ role, content, attachments, isLastUserMessage, onEditSubmit, onActionClick, size = 'md' }, ref) => {
        const t = useTranslations();
        const isUser = role === 'user';
        const actionMatch = isUser ? content.match(/^\s*\[ACTION:(.+?)\]/) : null;

        const displayContent = content;
        
        const [isEditing, setIsEditing] = useState(false);
        const [editValue, setEditValue] = useState(content);

        const getFileIcon = (type: string, size = 12) => {
            if (type === 'application/pdf') return <IconFileTypePdf size={size} />;
            if (type.startsWith('image/')) return <IconPhoto size={size} />;
            if (type.includes('csv')) return <IconFileTypeCsv size={size} />;
            if (type.startsWith('text/')) return <IconFileText size={size} />;
            return <IconPaperclip size={size} />;
        };

        // Si c'est une action rapide
        if (actionMatch) {
            const actionLabel = actionMatch[1];
            return (
                <Paper
                    ref={ref}
                    withBorder
                    px="sm"
                    py={6}
                    radius="xl"
                    bg="nli-liberty.0"
                    style={{
                        alignSelf: 'flex-end',
                        borderColor: 'var(--mantine-color-nli-liberty-4)',
                    }}
                >
                    <Group gap={6}>
                        <IconBolt size={14} color="var(--mantine-color-nli-liberty-6)" />
                        <Text size={size === 'md' ? 'sm' : 'xs'} fw={600} c="nli-liberty.7" style={{ textTransform: 'capitalize' }}>
                            {t('action-requested')}
                            {' '}
                            {actionLabel}
                        </Text>
                    </Group>
                </Paper>
            );
        }

        // Mode édition
        if (isUser && isEditing) {
            return (
                <Stack ref={ref} p="sm" style={{ alignSelf: 'flex-end', width: '100%', maxWidth: '85%' }}>
                    <Textarea 
                        value={editValue} 
                        onChange={(e) => setEditValue(e.currentTarget.value)} 
                        autosize 
                        minRows={2}
                        maxRows={6}
                        autoFocus
                        size={size}
                    />
                    <Group justify="flex-end" gap="sm">
                        <Button variant="subtle" color="gray" size={size === 'md' ? 'sm' : 'xs'} onClick={() => setIsEditing(false)}>
                            {t('cancel')}
                        </Button>
                        <Button
                            size={size === 'md' ? 'sm' : 'xs'}
                            color="nli-liberty"
                            onClick={() => {
                                setIsEditing(false);
                                if (editValue.trim() !== content) {
                                    onEditSubmit?.(editValue);
                                }
                            }}
                        >
                            {t('update')}
                        </Button>
                    </Group>
                </Stack>
            );
        }

        // Rendu normal
        return (
            <Group align="flex-start" wrap="nowrap" justify={isUser ? 'flex-end' : 'flex-start'} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: isUser ? '85%' : '100%', width: isUser ? undefined : '100%' }}>
                {/* Icône Assistant */}
                {!isUser && (
                    <ThemeIcon variant="light" color="nli-liberty.6" radius="xl" size="md">
                        <IconSparkles size={18} />
                    </ThemeIcon>
                )}

                {/* Bouton d'édition */}
                {isUser && isLastUserMessage && (
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => {
                            setEditValue(content);
                            setIsEditing(true);
                        }}
                    >
                        <IconEdit size={20} />
                    </ActionIcon>
                )}

                <Paper
                    ref={ref}
                    p={isUser ? 'sm' : 0}
                    radius={isUser ? 'md' : 0}
                    bg={isUser ? 'nli-liberty.7' : 'transparent'}
                    c={isUser ? 'white' : 'text'}
                    shadow="none"
                    style={{ overflow: 'visible', flex: isUser ? undefined : 1, minWidth: 0, border: isUser ? undefined : 'none' }}
                >
                    {isUser ? (
                    // Affichage texte simple pour l'utilisateur
                        <Stack gap="xs">
                            {attachments && attachments.length > 0 && (
                                <Group gap="xs">
                                    {attachments.map((att, idx) => (
                                        <Badge key={idx} variant="outline" color="white" leftSection={getFileIcon(att.type, 12)} style={{ textTransform: 'none' }}>
                                            {att.name}
                                        </Badge>
                                    ))}
                                </Group>
                            )}
                            {content && (
                                <Text size={size} style={{ whiteSpace: 'pre-wrap' }}>
                                    {content}
                                </Text>
                            )}
                        </Stack>
                    ) : (
                    // Affichage Markdown pour l'IA
                        <TypographyStylesProvider fz={size} p={0}>
                            <div className="markdown-content" style={{ margin: 0, padding: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        p: ({ node, ...props }) => (
                                            <p style={{ margin: '0 0 0.5em 0' }} {...props} />
                                        ),
                                        pre: ({ node, children, ...props }: any) => {
                                            const isUiBlock = node.children?.some(
                                                (child: any) => child.tagName === 'code' && child.properties?.className?.includes('language-ui'),
                                            );
                                            if (isUiBlock) return <div style={{ margin: 0, padding: 0 }}>
                                                {children}
                                            </div>;
                                            
                                            // Extract raw text for copy button
                                            let rawText = '';
                                            if (node.children?.[0]?.children?.[0]?.value) {
                                                rawText = node.children[0].children[0].value;
                                            }

                                            return (
                                                <div style={{ position: 'relative' }}>
                                                    <pre {...props} style={{ paddingRight: '40px', overflowX: 'auto', maxWidth: '100%' }}>
                                                        {children}
                                                    </pre>
                                                    {rawText && (
                                                        <CopyButton value={rawText} timeout={2000}>
                                                            {({ copied, copy }) => (
                                                                <Tooltip label={copied ? t('copied', { fallback: 'Copié' }) : t('copy', { fallback: 'Copier' })} withArrow position="left">
                                                                    <ActionIcon 
                                                                        color={copied ? 'teal' : 'gray'} 
                                                                        variant="subtle" 
                                                                        onClick={copy}
                                                                        style={{ position: 'absolute', top: '8px', right: '8px' }}
                                                                    >
                                                                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            )}
                                                        </CopyButton>
                                                    )}
                                                </div>
                                            );
                                        },
                                        code: ({ node, className, children, ...props }) => {
                                            const match = /language-(\w+)/.exec(className || '');
                                            if (match && match[1] === 'ui') {
                                                try {
                                                    const rawString = String(children).trim();
                                                    const parsedUi = JSON.parse(rawString);                                                    
                                                    
                                                    return (
                                                        <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                                                            <UiTreeRenderer 
                                                                node={parsedUi} 
                                                                onAction={onActionClick} 
                                                            />
                                                        </div>
                                                    );
                                                } catch {
                                                    // On masque l'erreur car elle se produit naturellement pendant le streaming du JSON
                                                    return <Skeleton height={60} radius="md" animate />;
                                                }
                                            }
                                            return <code className={className} {...props}>
                                                {children}
                                            </code>;
                                        },
                                    }}
                                >
                                    {displayContent}
                                </ReactMarkdown>
                            </div>
                        </TypographyStylesProvider>
                    )}
                </Paper>
            </Group>
        );
    },
);

ChatMessageBubble.displayName = 'ChatMessageBubble';